/**
 * Utility functions for processing bill titles
 */

/**
 * Determines if a title is too generic or unhelpful
 * @param {string} title - The main title part
 * @returns {boolean} - True if the title should be replaced with action
 */
const isGenericTitle = (title) => {
  if (!title || typeof title !== 'string') return false;
  
  const genericTerms = [
    'Income tax', 'Health', 'Education', 'Labor', 'Public Health', 'State Board', 
    'Banking', 'Behavioral Health', 'Courts', 'Elections', 'State symbols',
    'Guardian and ward', 'Professional licensing boards'
  ];
  
  // Check if it's a county name
  if (title.includes('County') || title.includes('county')) {
    return true;
  }
  
  // Check if it's a generic term
  if (genericTerms.includes(title)) {
    return true;
  }
  
  // Check if it's too short (likely generic)
  if (title.length < 15) {
    return true;
  }
  
  return false;
};

/**
 * Extracts a clean, readable bill title from semicolon-separated title strings
 * @param {string} title - The raw title from MongoDB (e.g., "Pediatric Health Safe Storage Act; enact")
 * @returns {string} - Clean title (e.g., "Pediatric Health Safe Storage Act")
 */
export const extractCleanTitle = (title) => {
  if (!title || typeof title !== 'string') {
    return title || '';
  }
  
  // If there's a semicolon, take everything before it
  if (title.includes(';')) {
    const parts = title.split(';');
    return parts[0].trim();
  }
  
  // If no semicolon, return the title as-is
  return title.trim();
};

/**
 * Extracts the action/description part from semicolon-separated title strings
 * @param {string} title - The raw title from MongoDB
 * @returns {string} - Action/description part (e.g., "enact", "require universal background checks")
 */
export const extractActionDescription = (title) => {
  if (!title || typeof title !== 'string') {
    return '';
  }
  
  if (title.includes(';')) {
    const parts = title.split(';');
    return parts.slice(1).join(';').trim(); // Join in case there are multiple semicolons
  }
  
  return '';
};

/**
 * Creates a smart, readable title by using action when main title is generic
 * @param {string} title - The raw title from MongoDB
 * @returns {string} - Smart, readable title
 */
export const createSmartTitle = (title) => {
  if (!title || typeof title !== 'string') {
    return title || '';
  }
  
  if (title.includes(';')) {
    const parts = title.split(';');
    const mainTitle = parts[0].trim();
    const action = parts.slice(1).join(';').trim();
    
    // If main title is generic, use the action part
    if (isGenericTitle(mainTitle)) {
      // Clean up the action part
      let cleanAction = action;
      
      // Remove common action words at the end
      const actionEndings = ['; enact', '; provide', '; require', '; authorize', '; establish', '; revise'];
      for (const ending of actionEndings) {
        if (cleanAction.endsWith(ending)) {
          cleanAction = cleanAction.slice(0, -ending.length);
        }
      }
      
      // Capitalize first letter
      if (cleanAction && cleanAction[0] === cleanAction[0].toLowerCase()) {
        cleanAction = cleanAction.charAt(0).toUpperCase() + cleanAction.slice(1);
      }
      
      return cleanAction || mainTitle;
    }
    
    // Otherwise, use the main title
    return mainTitle;
  }
  
  return title.trim();
};

/**
 * Creates a full readable title by combining the clean title with action
 * @param {string} title - The raw title from MongoDB
 * @returns {string} - Full readable title
 */
export const createReadableTitle = (title) => {
  if (!title || typeof title !== 'string') {
    return title || '';
  }
  
  const cleanTitle = extractCleanTitle(title);
  const action = extractActionDescription(title);
  
  if (action) {
    return `${cleanTitle}: ${action}`;
  }
  
  return cleanTitle;
};

/**
 * Gets a smart, readable bill title from a bill object
 * @param {Object} bill - The bill object
 * @returns {string} - Smart, readable title
 */
export const getReadableTitle = (bill) => {
  if (!bill) return '';
  
  // First, check if we have a generated display title
  if (bill.display_title) {
    return bill.display_title;
  }
  
  let title = bill.title;
  
  // Handle array titles
  if (Array.isArray(title)) {
    title = title.join(' ');
  }
  
  // Handle string titles
  if (typeof title === 'string') {
    // Use smart title extraction
    if (title.includes(';')) {
      return createSmartTitle(title);
    }
    
    // Handle comma-separated titles
    if (title.includes(',')) {
      const parts = title.split(',').map(s => s.trim()).filter(Boolean);
      // If the first part is substantial, use it; otherwise join all
      if (parts[0] && parts[0].length > 10) return parts[0];
      return parts.join(' ');
    }
    
    // Clean up whitespace and capitalize
    title = title.replace(/\s+/g, ' ').trim();
    if (title && title[0] === title[0].toLowerCase()) {
      title = title.charAt(0).toUpperCase() + title.slice(1);
    }
    
    if (title.length > 0) return title;
  }
  
  // Fallbacks
  if (bill.summary && bill.summary.length > 0) return bill.summary;
  if (bill.description && bill.description.length > 0) return bill.description;
  return bill.bill_number || 'Untitled Bill';
};

/**
 * Gets a short title for display (truncated if too long)
 * @param {Object} bill - The bill object
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Short title
 */
export const getShortTitle = (bill, maxLength = 80) => {
  const title = getReadableTitle(bill);
  
  if (title.length <= maxLength) {
    return title;
  }
  
  // Truncate at word boundary
  const truncated = title.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  if (lastSpaceIndex > maxLength * 0.75) {
    return truncated.substring(0, lastSpaceIndex) + '...';
  }
  
  return truncated + '...';
}; 